import Dinero from 'dinero.js'; // should come from dealer config

import Numeral from 'numeral';
import _get from 'lodash/get';
import _isNaN from 'lodash/isNaN';
import { getCurrencyCode as _getCurrencyCode } from '@tekion/tekion-i18n/utils'; // eslint-disable-line

import Currency from '@tekion/tekion-base/utils/currency'; // eslint-disable-line

import { EMPTY_OBJECT } from '../app.constants';
export var CURRENCY_PRECISION_MULTIPLIER = 100;
var DEFAULT_MONEY_FORMAT = '0.00';
var DEFAULT_PERCENTAGE_FORMAT = '0.0000';
export var DEFAULT_PERCENTAGE_PRECISION = 4;
var DEFAULT_TAX_PERCENTAGE = 9.25;
var DEFAULT_CURRENCY_PRECISION = 2;
export var getCurrencyCode = function getCurrencyCode() {
  if (Currency.currencyCode) return Currency.currencyCode;
  return _getCurrencyCode();
};
export var getCurrencyPrecision = function getCurrencyPrecision() {
  var currencyPrecision = Currency.currencyPrecision;
  if (currencyPrecision) return currencyPrecision;
  return DEFAULT_CURRENCY_PRECISION;
};

var getSanitizedValue = function getSanitizedValue(value) {
  return Numeral(value.toString().replace(/,/g, '').trim());
};

var getCurrencyWithoutPrecision = function getCurrencyWithoutPrecision(value) {
  if (_isNaN(parseInt(value, 10))) return 0;
  var currencyWithoutPrecision = parseInt(getSanitizedValue(value).multiply(CURRENCY_PRECISION_MULTIPLIER).value(), 10);
  return _isNaN(currencyWithoutPrecision) ? 0 : currencyWithoutPrecision;
};

var getCurrencyWithPrecision = function getCurrencyWithPrecision(value) {
  if (_isNaN(parseInt(value, 10))) return 0;
  return getSanitizedValue(value).divide(CURRENCY_PRECISION_MULTIPLIER).value();
};

export var getValue = function getValue(val, isInt) {
  if (_isNaN(parseInt(val, 10))) return 0;
  return isInt ? parseInt(val, 10) : getCurrencyWithoutPrecision(val);
};

var doOperation = function doOperation(val1, val2, operation) {
  var _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : EMPTY_OBJECT,
      _ref$isInt = _ref.isInt,
      isInt = _ref$isInt === void 0 ? false : _ref$isInt,
      _ref$formatResult = _ref.formatResult,
      formatResult = _ref$formatResult === void 0 ? true : _ref$formatResult,
      _ref$currencyCode = _ref.currencyCode,
      currencyCode = _ref$currencyCode === void 0 ? getCurrencyCode() : _ref$currencyCode;

  var newVal1 = getValue(val1, isInt);
  var newVal2 = getValue(val2, isInt);
  var amount = Numeral(newVal1)[operation](newVal2).value();
  if (formatResult) return Dinero({
    amount: amount,
    currency: currencyCode
  }).toFormat(DEFAULT_MONEY_FORMAT);
  return amount;
};

export var taxAmount = function taxAmount(amount) {
  return percentage(amount, DEFAULT_TAX_PERCENTAGE);
};
export var amountWithTax = function amountWithTax(amount, options) {
  var tax = taxAmount(amount);
  return format(add(amount, tax), options);
};

var multiply = function multiply(val1, val2) {
  var _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EMPTY_OBJECT,
      _ref2$currencyCode = _ref2.currencyCode,
      currencyCode = _ref2$currencyCode === void 0 ? getCurrencyCode() : _ref2$currencyCode;

  return Dinero({
    amount: parseInt(Numeral(val1).multiply(val2).value(), 10),
    currency: currencyCode
  }).toFormat(DEFAULT_MONEY_FORMAT);
};

var divide = function divide(val1, val2) {
  var _ref3 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EMPTY_OBJECT,
      _ref3$currencyCode = _ref3.currencyCode,
      currencyCode = _ref3$currencyCode === void 0 ? getCurrencyCode() : _ref3$currencyCode;

  if (!val2) return 0;
  return Dinero({
    amount: parseInt(Numeral(val1).divide(val2).value(), 10),
    currency: currencyCode
  }).toFormat(DEFAULT_MONEY_FORMAT);
};

var percentage = function percentage(amount, percent) {
  return parseInt(Numeral(amount).multiply(percent).divide(100).value(), 10);
};

var percentageInDecimal = function percentageInDecimal(amount, percent) {
  var decimalPlace = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_CURRENCY_PRECISION;
  return parseFloat(Numeral(amount).multiply(percent).divide(100).value().toFixed(decimalPlace));
};

var percentageOf = function percentageOf(baseAmount, totalAmount) {
  var precisionFormat = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_PERCENTAGE_FORMAT;
  return Numeral(totalAmount).multiply(100).divide(baseAmount).format(precisionFormat);
};

var add = function add(val1, val2, options) {
  return doOperation(val1, val2, 'add', options);
};

var format = function format(val, options) {
  var optionParams = options || EMPTY_OBJECT;
  var _optionParams$currenc = optionParams.currencyCode,
      currencyCode = _optionParams$currenc === void 0 ? getCurrencyCode() : _optionParams$currenc;
  var newVal = getValue(val, optionParams.isInt);
  return Dinero({
    amount: newVal,
    currency: currencyCode
  }).toFormat(DEFAULT_MONEY_FORMAT);
};

var moneyUtils = {
  multiply: multiply,
  divide: divide,
  percentage: percentage,
  percentageInDecimal: percentageInDecimal,
  add: add,
  format: format,
  percentageOf: percentageOf,
  subtract: function subtract(val1, val2, options) {
    return doOperation(val1, val2, 'subtract', options);
  },
  toInt: function toInt(val) {
    return getCurrencyWithoutPrecision(val);
  },
  withPrecision: function withPrecision(val) {
    return getCurrencyWithPrecision(val);
  },
  create: function create(val, options) {
    var optionParams = options || EMPTY_OBJECT;
    var _optionParams$currenc2 = optionParams.currencyCode,
        currencyCode = _optionParams$currenc2 === void 0 ? getCurrencyCode() : _optionParams$currenc2;
    return {
      amount: optionParams.isInt ? val : getCurrencyWithoutPrecision(val),
      currency: currencyCode
    };
  },
  currencyCode: function currencyCode() {
    return getCurrencyCode();
  }
};
export var parsePriceToTMoney = moneyUtils.create;
export var getPriceDetailsFromTMoney = function getPriceDetailsFromTMoney(moneyDetails) {
  return {
    amount: getCurrencyWithPrecision(_get(moneyDetails, 'amount')),
    currency: getCurrencyCode()
  };
};
export default moneyUtils;
export { Dinero };